import React, { memo, useMemo, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useOffline } from './hooks/useOffline';
import Login from './components/auth/Login';
import ForgotPassword from './components/auth/ForgotPassword';
import UpdatePassword from './components/auth/UpdatePassword';
import OfflineOverlay from './components/offline/OfflineOverlay';
import './App.css';
import MainContext from './contexts';

// Lazy load heavy components to reduce initial bundle size
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const ManagerDashboard = lazy(() => import('./components/manager/ManagerDashboard'));
const EmployeeDashboard = lazy(() => import('./components/employee/EmployeeDashboard'));

const DashboardFallback = ({ label }) => (
  <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    <span className="ml-2">{label}</span>
  </div>
);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role) && !allowedRoles.includes(user?.userType)) {
    return <Navigate to={getUserDashboardPath(user)} replace />;
  }

  return children;
};

const getUserDashboardPath = (user) => {
  if (user?.adminType || user?.role === 'admin') {
    return '/admin/dashboard';
  }
  if (user?.userType === 'employee' || user?.role === 'employee') {
    return '/employee/overview';
  }
  return '/manager/dashboard';
};

const AppRoutes = memo(({ isAuthenticated, dashboardPath }) => (
  <Routes>
    <Route
      path="/login"
      element={isAuthenticated ? <Navigate to={dashboardPath} replace /> : <Login />}
    />

    <Route
      path="/forgot-password"
      element={isAuthenticated ? <Navigate to={dashboardPath} replace /> : <ForgotPassword />}
    />

    <Route
      path="/update-password"
      element={isAuthenticated ? <Navigate to={dashboardPath} replace /> : <UpdatePassword />}
    />

    <Route
      path="/dashboard"
      element={
        <ProtectedRoute>
          <Navigate to={dashboardPath} replace />
        </ProtectedRoute>
      }
    />

    <Route
      path="/admin/*"
      element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Suspense fallback={<DashboardFallback label="Loading Admin Dashboard..." />}>
            <AdminDashboard />
          </Suspense>
        </ProtectedRoute>
      }
    />

    <Route
      path="/manager/*"
      element={
        <ProtectedRoute allowedRoles={['manager', 'supervisor']}>
          <Suspense fallback={<DashboardFallback label="Loading Manager Dashboard..." />}>
            <ManagerDashboard />
          </Suspense>
        </ProtectedRoute>
      }
    />

    <Route
      path="/employee/*"
      element={
        <ProtectedRoute allowedRoles={['employee']}>
          <Suspense fallback={<DashboardFallback label="Loading Employee Dashboard..." />}>
            <EmployeeDashboard />
          </Suspense>
        </ProtectedRoute>
      }
    />

    <Route
      path="/"
      element={<Navigate to={isAuthenticated ? dashboardPath : '/login'} replace />}
    />

    <Route
      path="*"
      element={<Navigate to={isAuthenticated ? dashboardPath : '/login'} replace />}
    />
  </Routes>
));

const AppContent = memo(() => {
  const { user, isAuthenticated } = useAuth();
  const { isOffline } = useOffline();

  const dashboardPath = useMemo(() => {
    if (!user) return '/login';
    return getUserDashboardPath(user);
  }, [user]);

  return (
    <div className="min-h-screen bg-secondary-50 webview-container">
      {isAuthenticated && isOffline && <OfflineOverlay />}
      <AppRoutes
        isAuthenticated={isAuthenticated}
        dashboardPath={dashboardPath}
      />
    </div>
  );
});

const App = memo(() => (
  <Router>
    <MainContext>
      <AppContent />
    </MainContext>
  </Router>
));

export default App;
